import { useEffect, useEffectEvent, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore, useStoreStore } from '@/stores';
import { useUser } from '@/queries/useUser';
import { useExperiments } from '@/features/Experiments/hooks';
import { useUserflow } from './UserflowProvider';
import { useClarity } from './ClarityProvider';
import { SUBSCRIPTION_TIERS } from '@/lib/api/subscriptions/types';
import { AFFILIATE_MENUITEMS, NO_AUTH_ROUTES } from '@/lib/constants';
import { hasActiveSubscription } from '@/features/subscriptions/logic/subscriptionStatus.logic';

export function UserProvider({ children }: { children: React.ReactNode }) {
    const { user } = useUser();
    const location = useLocation();
    const navigate = useNavigate();
    const updateCurrentAccountWithUserData = useAuthStore(state => state.updateCurrentAccountWithUserData);
    const setStoreId = useStoreStore(state => state.setDefaultStoreId);
    const isAdminLoggedInAsUser = useAuthStore(state => state.isAdminLoggedInAsUser());
    const { identifyInUserflow, reidentifyInUserflow } = useUserflow();
    const { identifyInClarity, setDaysFromSignup, setExperiments, isReady: isClarityReady } = useClarity();
    const clarityExperimentSetRef = useRef(false);

    // Handles experiment initialization and assignment
    useExperiments();

    const handleUpdateAccount = useEffectEvent((userData: typeof user) => {
        if (userData) {
            updateCurrentAccountWithUserData(userData);
            setStoreId(userData.default_store_id);
        }
    });

    // Update auth store when user data is fetched - moved from useUser hook
    useEffect(() => {
        handleUpdateAccount(user);
    }, [user]);

    const shouldTrack = useMemo(() => {
        if (!user) return false;
        const isSuperAdmin = user.list_of_roles?.includes(1);
        return !isAdminLoggedInAsUser && !isSuperAdmin;
    }, [user, isAdminLoggedInAsUser]);

    const handleIdentifyUser = useEffectEvent((userData: typeof user) => {
        if (!userData) return;
        identifyInUserflow(userData);
        if (isClarityReady()) {
            identifyInClarity(userData);
        }
    });

    const handleReidentifyUser = useEffectEvent((userData: typeof user) => {
        if (userData) {
            reidentifyInUserflow(userData);
        }
    });

    const handleSetClarityData = useEffectEvent((userData: typeof user) => {
        if (!userData || !isClarityReady() || clarityExperimentSetRef.current) return;

        clarityExperimentSetRef.current = true;

        // Set days from signup
        setDaysFromSignup(userData.created_at);

        // Set all experiments as tags
        if (userData.experiments) {
            setExperiments(userData.experiments);
        }
    });

    // Identify user in Userflow and Clarity
    useEffect(() => {
        if (shouldTrack) {
            handleIdentifyUser(user);
        }
    }, [user, shouldTrack]);

    // Re-identify in Userflow when profile image changes
    useEffect(() => {
        if (user?.data?.profile_image && shouldTrack) {
            handleReidentifyUser(user);
        }
    }, [user?.data?.profile_image, user, shouldTrack]);

    // Set Clarity experiments and days from signup (once per session)
    useEffect(() => {
        handleSetClarityData(user);
    }, [user]);

    // Track the last path we checked to prevent infinite loops
    const lastCheckedPathRef = useRef<string | null>(null);

    // Subscription and affiliate route restrictions (similar to Vue's subscription-check middleware)
    useEffect(() => {
        const currentPath = location.pathname;

        // Don't check on auth routes
        if (NO_AUTH_ROUTES.some(route => currentPath.startsWith(route))) {
            return;
        }

        // Don't check if user is not loaded yet
        if (!user) {
            return;
        }

        // Skip if we've already checked this exact path
        if (lastCheckedPathRef.current === currentPath) {
            return;
        }

        // Check 1: Subscription check
        // If user has no subscription history AND no active subscription, redirect to subscribe
        // We check both because has_subscription_history might not be updated immediately after subscription creation
        const userHasActiveSubscription = hasActiveSubscription(user);

        // Check if subscription was just created - this flag is set after successful payment
        // to prevent redirect loop while user data is being updated
        const subscriptionJustCreated = sessionStorage.getItem('subscription_just_created') === 'true';
        if (subscriptionJustCreated) {
            // Clear the flag once we've checked it
            sessionStorage.removeItem('subscription_just_created');
            // Skip subscription check - user just subscribed
        } else if (!user.has_subscription_history && !userHasActiveSubscription && !isAdminLoggedInAsUser) {
            // Allow access to subscribe page itself
            if (!currentPath.startsWith('/subscribe')) {
                lastCheckedPathRef.current = '/subscribe/plan';
                navigate('/subscribe/plan', { replace: true });
                return;
            }
        }

        // Check 2: Affiliate tier restriction
        // If user is affiliate tier, only allow access to affiliate-allowed routes
        if (user.subscription_tier === SUBSCRIPTION_TIERS.AFFILIATE) {
            // Get allowed routes from AFFILIATE_MENUITEMS
            const allowedRoutes = AFFILIATE_MENUITEMS.map(item => item.route).filter((route): route is string =>
                Boolean(route)
            );

            // Also allow income details pages (e.g., /income/details/:paymentId)
            const isIncomeDetailsPage = currentPath.startsWith('/income/details/');

            // Allow integration authorization page (used for Stripe/PayPal OAuth connection flows)
            const isIntegrationAuthPage = currentPath.startsWith('/integration-authorization');

            // Check if current route matches any allowed route
            // Normalize paths by removing query params and trailing slashes for comparison
            const pathWithoutQuery = currentPath.split('?')[0];
            const normalizedCurrentPath = pathWithoutQuery ? pathWithoutQuery.replace(/\/$/, '') : '';
            const isAllowedRoute =
                allowedRoutes.length > 0 &&
                allowedRoutes.some(route => {
                    if (!route) return false;
                    const normalizedRoute = route.replace(/\/$/, '');
                    // Exact match
                    if (normalizedCurrentPath === normalizedRoute) {
                        return true;
                    }
                    // Check if current path starts with the route (for nested routes like /account_settings?tab=store)
                    if (normalizedCurrentPath.startsWith(normalizedRoute + '/')) {
                        return true;
                    }
                    return false;
                });

            // If not an allowed route and not a special page, redirect to affiliate
            if (!isAllowedRoute && !isIncomeDetailsPage && !isIntegrationAuthPage && currentPath !== '/affiliate') {
                lastCheckedPathRef.current = '/affiliate';
                navigate('/affiliate', { replace: true });
                return;
            }
        }

        // Mark this path as checked
        lastCheckedPathRef.current = currentPath;
    }, [location.pathname, user, isAdminLoggedInAsUser, navigate]);

    // TODO: Validate if we need this, because we are not using it
    // useEffect(() => {
    //     if (user?.data.color_code) {
    //         document.documentElement.style.setProperty('--stan-user-primary-color', user.data.color_code);
    //     }
    // }, [user?.data.color_code]);

    return <>{children}</>;
}
